import { ServiceResponse } from './../responses/service-response';
import { HttpClient, HttpErrorResponse, HttpResponse, HttpHeaders, HttpEventType } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { identity, Observable, of, timer } from 'rxjs';
import { catchError, map, retry, shareReplay, switchMap, take, tap, timeout } from 'rxjs/operators';
import { BaseError } from '../messages/base-error';
import { ServiceRequest } from '../requests/service-request';
import { EndPointResolver } from './end-point-resolver';
import { ResponseCacheService, WITHOUT_EXPIRATION_TIME } from '../responses/response-cache.service';
import { MessageCodes } from '../resources/message-codes';
import { MessageResourceManager } from '../resources/message-resource-manager';
import { LogService, OnlineService, TIMEOUT_CACHE } from '@nts/std/utility';
import { classToPlain, ClassConstructor, plainToClass, plainToClassFromExist } from '@nts/std/serialization';
import { JsonNetDecycle } from '@nts/std/serialization';
import { ServiceInfo } from './service-info';
import { ResponseWithCacheInfo } from '../responses/response-with-cache-info';
import { CacheOptionsInterface, ServiceRequestInterface, ServiceResponseInterface, WebApiServiceAgentInterface } from '@nts/std/interfaces';
import { AuthRequestService } from '../auth/auth-request.service';

@Injectable()
export class WebApiServiceAgent implements WebApiServiceAgentInterface {

    rootModelName: string;

    constructor(
        private readonly httpService: HttpClient,
        private readonly endPointResolver: EndPointResolver,
        private readonly responseCache: ResponseCacheService,
        private readonly onlineService: OnlineService,
        // @Optional()
        // @Inject(SERIALIZATION_WEBWORKER_FACTORY) private readonly serializationWebworkerFactory: () => Worker,
        // @Optional()
        // @Inject(RESPONSE_CACHE_SETTINGS_WEBWORKER_FACTORY) private readonly responseCacheWebworkerSettings: ResponseCacheWebWorkerSettings
    ) {

    }

    getHttpService(): HttpClient {
        return this.httpService;
    }

    getServiceInfo(
        useFrameworkService: boolean,
        customControllerName: string = null
    ): ServiceInfo {
        return useFrameworkService ? this.endPointResolver.getWebApiUriForFrameworkService() :
            this.endPointResolver.getWebApiUriForRootDomainModel(customControllerName ?? this.rootModelName);;
    }

    invokePostWithOutBodyWithInstance<TResponse extends ServiceResponseInterface>(
        requestUri: string,
        responseInstance: TResponse,
        useFrameworkService = false,
        customHeaders: HttpHeaders = null,
        customControllerName: string = null,          // example: TipoBollaFatturaReport
        customControllerAddress: string = null,       // example: https://ntscompanydata.ntsinformatica.it/api/TipoBollaFatturaReport
        cacheOptions: CacheOptionsInterface = {
            bypass: false,
            expirationTime: undefined,
            force: false,
        },
        retryRequest: number = 0,
    ): Observable<TResponse> {
        const requestOptions = {
            headers: customHeaders,
        };

        return this.handleRequestWithInstance(
            'POST',
            requestUri,
            null,
            requestOptions,
            responseInstance,
            useFrameworkService,
            customControllerName,
            customControllerAddress,
            cacheOptions,
            retryRequest
        );
    }

    // TODO ????
    invokeGetWithOutQueryWithInstance<TResponse extends ServiceResponseInterface>(
        requestUri: string,
        responseInstance: TResponse,
        useFrameworkService = false,
        customHeaders: HttpHeaders = null,
        customControllerName: string = null,          // example: TipoBollaFatturaReport
        customControllerAddress: string = null,       // example: https://ntscompanydata.ntsinformatica.it/api/TipoBollaFatturaReport,
        cacheOptions: CacheOptionsInterface = {
            bypass: false,
            expirationTime: undefined,
            force: false,
        },
        retryRequest: number = 0,
    ): Observable<TResponse> {

        const serviceInfo = this.getServiceInfo(useFrameworkService, customControllerName);
        const url: string = (customControllerAddress ?? serviceInfo.address) + '/' + requestUri;

        return this.onlineService.isOnline$.pipe(
            take(1),
            switchMap(() => {
                if (this.onlineService.isOnline) {
                    if (!cacheOptions.bypass) {
                        return this.responseCache
                            .check(
                                responseInstance,
                                url,
                                null,
                                this.onlineService.isOnline,
                                cacheOptions.expirationTime,
                                null,
                                cacheOptions.force
                            )
                            .pipe(
                                switchMap((isInCache) => {
                                    if (isInCache) {
                                        return this.responseCache
                                            .get(
                                                responseInstance,
                                                url,
                                                null,
                                                this.onlineService.isOnline,
                                                cacheOptions.expirationTime,
                                                null,
                                                cacheOptions.force
                                            )
                                            .pipe(
                                                switchMap((cachedData) => {
                                                    LogService.debug(
                                                        `Used cache for ${url}`
                                                    );
                                                    return of(this.handleResponse<TResponse>(
                                                        responseInstance,
                                                        cachedData,
                                                        true
                                                    ));
                                                })
                                            );
                                    } else {
                                        const options = {
                                            headers: customHeaders,
                                        };

                                        return this.sendRequest(
                                            "GET",
                                            url,
                                            options,
                                            retryRequest
                                        ).pipe(
                                            switchMap((rawResponse: object | HttpResponse<any>) => {
                                                LogService.debug(
                                                    "GET sendRequest",
                                                    rawResponse,
                                                    url,
                                                    options
                                                );

                                                const res = this.handleResponse<TResponse>(
                                                    responseInstance,
                                                    rawResponse,
                                                    true
                                                );

                                                if (!cacheOptions.bypass) {

                                                    // Se la chiamata è andata a buon fine
                                                    if (res?.errors?.length == 0 && res?.operationSuccedeed == true) {
                                                        this.responseCache.set(
                                                            responseInstance,
                                                            url,
                                                            rawResponse instanceof HttpResponse ? rawResponse.body : rawResponse,
                                                            null,
                                                            cacheOptions.force
                                                        ).subscribe((res) => {
                                                            // if (this.responseCacheWebworkerSettings?.showLogs) {
                                                            //     LogService.debug('Risultato salvataggio cache: ' + res)
                                                            // }
                                                        });
                                                    }
                                                }

                                                return of(res);
                                            }),
                                            catchError((err) => {
                                                LogService.warn(
                                                    "invokeGetWithOutQueryWithInstance failed",
                                                    err,
                                                    url,
                                                    options
                                                );
                                                return this.handleErrorAsResponseWithInstance<TResponse>(
                                                    err,
                                                    responseInstance
                                                );
                                            })
                                        );
                                    }
                                })
                            );
                    } else {
                        const options = {
                            headers: customHeaders,
                        };

                        return this.sendRequest("GET", url, options).pipe(
                            switchMap((rawResponse: object | HttpResponse<any>) => {
                                LogService.debug(
                                    "GET sendRequest",
                                    rawResponse,
                                    url,
                                    options
                                );

                                const res = this.handleResponse<TResponse>(
                                    responseInstance,
                                    rawResponse,
                                    true
                                );

                                if (!cacheOptions.bypass) {

                                    // Se la chiamata è andata a buon fine
                                    if (res?.errors?.length == 0 && res?.operationSuccedeed == true) {
                                        this.responseCache.set(
                                            responseInstance,
                                            url,
                                            rawResponse instanceof HttpResponse ? rawResponse.body : rawResponse,
                                            null,
                                            cacheOptions.force
                                        ).subscribe((res) => {
                                            // if (this.responseCacheWebworkerSettings?.showLogs) {
                                            //     LogService.debug('Risultato salvataggio cache: ' + res)
                                            // }
                                        });
                                    }

                                }
                                return of(res);
                            }),
                            catchError((err) => {
                                LogService.warn(
                                    "invokeGetWithOutQueryWithInstance failed",
                                    err,
                                    url,
                                    options
                                );
                                return this.handleErrorAsResponseWithInstance<TResponse>(
                                    err,
                                    responseInstance
                                );
                            })
                        );
                    }
                } else {
                    return this.responseCache
                        .check(
                            responseInstance,
                            url,
                            null,
                            this.onlineService.isOnline,
                            cacheOptions.expirationTime,
                            null,
                            cacheOptions.force
                        )
                        .pipe(
                            switchMap((isInCache) => {
                                if (isInCache) {

                                    return this.responseCache.get(
                                        responseInstance,
                                        url,
                                        null,
                                        this.onlineService.isOnline,
                                        cacheOptions.expirationTime,
                                        null,
                                        cacheOptions.force
                                    ).pipe(switchMap((cachedData) => {
                                        LogService.debug(`Used cache for ${url}`);
                                        return of(this.handleResponse<TResponse>(
                                            responseInstance,
                                            cachedData,
                                            true
                                        ));
                                    }));
                                } else {
                                    const err: any = new Error(
                                        `No cache found for url ${url} with sended data.`
                                    );
                                    LogService.warn(
                                        "invokeGetWithOutQueryWithInstance failed in offline mode",
                                        err,
                                        url,
                                        cacheOptions
                                    );
                                    return this.handleErrorAsResponseWithInstance<TResponse>(
                                        err,
                                        responseInstance
                                    );
                                }
                            })
                        );
                }
            })
        );
    }

    invokeGetWithOutQuery<TResponse extends ServiceResponseInterface>(
        requestUri: string,
        responseType: ClassConstructor<TResponse>,
        useFrameworkService = false,
        customHeaders: HttpHeaders = null,
        customControllerName: string = null,          // example: TipoBollaFatturaReport
        customControllerAddress: string = null,       // example: https://ntscompanydata.ntsinformatica.it/api/TipoBollaFatturaReport,
        cacheOptions: CacheOptionsInterface = {
            bypass: false,
            expirationTime: undefined,
            force: false,
        },
        retryRequest: number = 0,
    ): Observable<TResponse> {

        const requestOptions = {
            headers: customHeaders,
        };

        return this.handleRequest(
            'GET',
            requestUri,
            undefined,
            requestOptions,
            responseType,
            useFrameworkService,
            customControllerName,
            customControllerAddress,
            cacheOptions,
            retryRequest
        );
    }

    invokePostUploadFile<TResponse extends ServiceResponseInterface>(
        requestUri: string,
        formData: FormData,
        responseInstance: TResponse,
        useFrameworkService = false,
        customHeaders: HttpHeaders = null,
        customControllerName: string = null,          // example: TipoBollaFatturaReport
        customControllerAddress: string = null,       // example: https://ntscompanydata.ntsinformatica.it/api/TipoBollaFatturaReport
        retryRequest: number = 0,
    ): Observable<TResponse> {

        const serviceInfo = this.getServiceInfo(useFrameworkService, customControllerName);
        const url: string = (customControllerAddress ?? serviceInfo.address) + '/' + requestUri;

        const options = {
            body: formData,
            observe: 'events',
            reportProgress: true,
            headers: customHeaders
        };

        return this.sendRequest('POST', url, options, retryRequest).pipe(
            map(response => {
                // LogService.debug('POST sendRequest', response, url, options);
                return response;
            }),
            catchError(err => {
                LogService.warn('invokePostUploadFile failed', err, url, options);
                return this.handleErrorAsResponseWithInstance<TResponse>(err, responseInstance);
            })
        );
    }

    invokePostUploadFileWithInstance<TResponse extends ServiceResponseInterface>(
        requestUri: string,
        formData: FormData,
        responseInstance: TResponse,
        useFrameworkService = false,
        customHeaders: HttpHeaders = null,
        customControllerName: string = null,          // example: TipoBollaFatturaReport
        customControllerAddress: string = null,        // example: https://ntscompanydata.ntsinformatica.it/api/TipoBollaFatturaReport
        retryRequest: number = 0,
    ): Observable<TResponse> {

        const serviceInfo = this.getServiceInfo(useFrameworkService, customControllerName);
        const url: string = (customControllerAddress ?? serviceInfo.address) + '/' + requestUri;

        const options = {
            body: formData,
            observe: 'events',
            reportProgress: true,
            headers: customHeaders
        };

        return this.sendRequest('POST', url, options, retryRequest).pipe(
            switchMap((httpEvent: any) => {
                if (httpEvent) {
                    if (httpEvent.type === HttpEventType.UploadProgress) {
                        LogService.debug(`Upload in progress: ${Math.round(100 * httpEvent.loaded / httpEvent.total)}`);
                        return of(httpEvent);
                    } else if (httpEvent.type === HttpEventType.Response) {
                        LogService.debug(`Upload complete:`, httpEvent.body);
                        return of(this.handleResponse<TResponse>(responseInstance, httpEvent.body, true));
                    }
                }
                return of(httpEvent);
            }),
            catchError(err => {
                LogService.warn('invokePostUploadFileWithInstance failed', err, url, options);
                return this.handleErrorAsResponseWithInstance<TResponse>(err, responseInstance);
            })
        );
    }

    private authRequestService: AuthRequestService = inject(AuthRequestService);

    invokePostStreamingWithInstance<TRequest extends ServiceRequest, TChunk>(
        requestUri: string,
        requestContent: TRequest,
        chunkFactory: (json: any) => TChunk,
        useFrameworkService = false,
        customHeaders: HttpHeaders = null,
        customControllerName: string = null,
        customControllerAddress: string = null,
        retryRequest: number = 0,
    ): Observable<TChunk> {

        const url: string = this.buildRequestUrl(
            requestUri,
            useFrameworkService,
            customControllerName,
            customControllerAddress
        );

        const cycledRequestPlain = this.serializeRequestBody(requestContent);

        return new Observable<TChunk>((observer) => {
            const abortController = new AbortController();
            const teardown = () => abortController.abort();

            if (!this.onlineService.isOnline) {
                observer.error(new Error(`Streaming request ${requestUri} not available in offline mode.`));
                return teardown;
            }

            const executeFetch = async () => {
                let response: Response;

                try {

                    const baseHeaders = this.buildFetchHeaders(customHeaders);
                    const headers = await this.authRequestService.buildHeaders(baseHeaders);

                    response = await fetch(url, {
                        method: 'POST',
                        headers,
                        body: JSON.stringify(cycledRequestPlain),
                        signal: abortController.signal
                    });
                } catch (err) {
                    if (!abortController.signal.aborted) {
                        LogService.warn(
                            `POST STREAM ${url} failed during fetch`,
                            err,
                            url,
                            cycledRequestPlain
                        );
                        observer.error(err);
                    }
                    return;
                }

                if (!response.ok || !response.body) {
                    const error = new Error(`Streaming request failed with status ${response.status}`);
                    LogService.warn(
                        `POST STREAM ${url} invalid response`,
                        error,
                        url,
                        cycledRequestPlain
                    );
                    observer.error(error);
                    return;
                }

                LogService.debug(`POST STREAM ${url} started`, url, cycledRequestPlain);

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';

                try {
                    while (true) {
                        const { done, value } = await reader.read();

                        if (done) {
                            break;
                        }

                        buffer += decoder.decode(value, { stream: true });

                        const lines = buffer.split(/\r?\n/);
                        buffer = lines.pop() ?? '';

                        for (const line of lines) {
                            const trimmed = line.trim();

                            if (!trimmed) {
                                continue;
                            }

                            if (!trimmed.startsWith('data:')) {
                                continue;
                            }

                            const payload = trimmed.slice(5).trim();

                            if (!payload || payload === '[DONE]') {
                                continue;
                            }

                            try {
                                const parsed = JSON.parse(payload);
                                const chunk = chunkFactory(parsed);
                                observer.next(chunk);
                            } catch (parseErr) {
                                LogService.warn(
                                    `POST STREAM ${url} malformed chunk`,
                                    parseErr,
                                    payload
                                );
                            }
                        }
                    }

                    const trailing = buffer.trim();
                    if (trailing.startsWith('data:')) {
                        const payload = trailing.slice(5).trim();

                        if (payload && payload !== '[DONE]') {
                            try {
                                const parsed = JSON.parse(payload);
                                const chunk = chunkFactory(parsed);
                                observer.next(chunk);
                            } catch (parseErr) {
                                LogService.warn(
                                    `POST STREAM ${url} malformed trailing chunk`,
                                    parseErr,
                                    payload
                                );
                            }
                        }
                    }

                    LogService.debug(`POST STREAM ${url} completed`, url);
                    observer.complete();
                } catch (err) {
                    if (!abortController.signal.aborted) {
                        LogService.warn(
                            `POST STREAM ${url} failed during read`,
                            err,
                            url
                        );
                        observer.error(err);
                    }
                } finally {
                    try {
                        reader.releaseLock();
                    } catch {
                        // ignore release errors
                    }
                }
            };

            void executeFetch();

            return teardown;
        });
    }

    private buildRequestUrl(
        requestUri: string,
        useFrameworkService: boolean = false,
        customControllerName: string = null,
        customControllerAddress: string = null,
    ): string {
        const serviceInfo = this.getServiceInfo(useFrameworkService, customControllerName);
        return (customControllerAddress ?? serviceInfo.address) + '/' + requestUri;
    }

    private serializeRequestBody<TRequest extends ServiceRequest>(requestContent: TRequest): Record<string, any> {
        const requestPlain = classToPlain(requestContent, { strategy: 'excludeAll' });
        return JsonNetDecycle.decycle(requestPlain);
    }

    private buildFetchHeaders(customHeaders: HttpHeaders = null): Record<string, string> {
        const headersObject: Record<string, string> = {
            'Content-Type': 'application/json'
        };

        if (customHeaders) {
            for (const key of customHeaders.keys()) {
                const value = customHeaders.get(key);
                if (value != null) {
                    headersObject[key] = value;
                }
            }
        }

        return headersObject;
    }

    invokePostDownloadFile<TRequest extends ServiceRequest, TResponse extends HttpResponse<Blob>>(
        requestUri: string,
        requestContent: TRequest,
        useFrameworkService = false,
        customHeaders: HttpHeaders = null,
        customControllerName: string = null,          // example: TipoBollaFatturaReport
        customControllerAddress: string = null,       // example: https://ntscompanydata.ntsinformatica.it/api/TipoBollaFatturaReport
        retryRequest: number = 0,
    ): Observable<HttpResponse<Blob>> {

        const serviceInfo = this.getServiceInfo(useFrameworkService, customControllerName);
        const url: string = (customControllerAddress ?? serviceInfo.address) + '/' + requestUri;

        const requestPlain = classToPlain(requestContent, { strategy: 'excludeAll' });
        const cycledRequestPlain = JsonNetDecycle.decycle(requestPlain);
        const options = {
            body: cycledRequestPlain,
            observe: 'events',
            responseType: 'blob',
            headers: customHeaders
        };

        return this.sendRequest('POST', url, options, retryRequest).pipe(
            map(response => {
                // LogService.debug('POST sendRequest', response, url, options);
                return response;
            }),
            catchError(err => {
                LogService.warn('invokePostDownloadFile failed', err, url, options);
                return this.handleErrorAsResponse(<TResponse>(err));
            })
        );
    }

    invokePostDownloadFileWithOutBody<TResponse extends Blob>(
        requestUri: string,
        useFrameworkService = false,
        customHeaders: HttpHeaders = null,
        customControllerName: string = null,          // example: TipoBollaFatturaReport
        customControllerAddress: string = null,       // example: https://ntscompanydata.ntsinformatica.it/api/TipoBollaFatturaReport
        retryRequest: number = 0,
    ): Observable<Blob> {

        const serviceInfo = this.getServiceInfo(useFrameworkService, customControllerName);
        const url: string = (customControllerAddress ?? serviceInfo.address) + '/' + requestUri;

        const options = {
            observe: 'events',
            responseType: 'blob',
            headers: customHeaders
        };

        return this.sendRequest('POST', url, options, retryRequest).pipe(
            map(response => {
                // LogService.debug('POST sendRequest', response, url, options);
                return response;
            }),
            catchError(err => {
                LogService.warn('invokePostDownloadFileWithOutBody failed', err, url, options);
                return this.handleErrorAsResponse(<TResponse>(err));
            })
        );
    }

    invokePostWithResponseWithInstance<TRequest extends ServiceRequestInterface, TResponse extends ServiceResponseInterface>(
        requestUri: string,
        requestContent: TRequest,
        responseInstance: TResponse,
        useFrameworkService = false,
        customHeaders: HttpHeaders = null,
        customControllerName: string = null,          // example: TipoBollaFatturaReport
        customControllerAddress: string = null,       // example: https://ntscompanydata.ntsinformatica.it/api/TipoBollaFatturaReport
        cacheOptions: CacheOptionsInterface = {
            bypass: false,
            expirationTime: undefined,
            force: false,
        },
        retryRequest: number = 0,
    ): Observable<TResponse> {

        const requestPlain = classToPlain(requestContent, { strategy: 'excludeAll' });
        const cycledRequestPlain = JsonNetDecycle.decycle(requestPlain);

        const requestOptions = {
            body: cycledRequestPlain,
            headers: customHeaders
        };

        return this.handleRequestWithInstance(
            'POST',
            requestUri,
            cycledRequestPlain,
            requestOptions,
            responseInstance,
            useFrameworkService,
            customControllerName,
            customControllerAddress,
            cacheOptions,
            retryRequest
        );
    }

    invokePostWithResponseWithInstanceWithCacheInfo<TRequest extends ServiceRequest, TResponse extends ServiceResponseInterface>(
        requestUri: string,
        requestContent: TRequest,
        responseInstance: TResponse,
        useFrameworkService = false,
        customHeaders: HttpHeaders = null,
        customControllerName: string = null,          // example: TipoBollaFatturaReport
        customControllerAddress: string = null,       // example: https://ntscompanydata.ntsinformatica.it/api/TipoBollaFatturaReport
        cacheOptions: CacheOptionsInterface = {
            bypass: false,
            expirationTime: undefined,
            force: false,
        },
        retryRequest: number = 0,
    ): Observable<ResponseWithCacheInfo<TResponse>> {

        const requestPlain = classToPlain(requestContent, { strategy: 'excludeAll' });
        const cycledRequestPlain = JsonNetDecycle.decycle(requestPlain);

        const requestOptions = {
            body: cycledRequestPlain,
            headers: customHeaders
        };

        return this.handleRequestWithInstanceWithCacheInfo(
            'POST',
            requestUri,
            cycledRequestPlain,
            requestOptions,
            responseInstance,
            useFrameworkService,
            customControllerName,
            customControllerAddress,
            cacheOptions,
            retryRequest
        );
    }

    invokePostWithOutResponse<TRequest extends ServiceRequest>(
        requestUri: string,
        requestContent: TRequest,
        useFrameworkService = false,
        customHeaders: HttpHeaders = null,
        customControllerName: string = null,          // example: TipoBollaFatturaReport
        customControllerAddress: string = null,       // example: https://ntscompanydata.ntsinformatica.it/api/TipoBollaFatturaReport,
        retryRequest: number = 0,
    ): Observable<ServiceResponseInterface> {

        const serviceInfo = this.getServiceInfo(useFrameworkService, customControllerName);
        const url: string = (customControllerAddress ?? serviceInfo.address) + '/' + requestUri;

        const requestPlain = classToPlain(requestContent, { strategy: 'excludeAll' });
        const cycledRequestPlain = JsonNetDecycle.decycle(requestPlain);

        const options = {
            body: cycledRequestPlain,
            headers: customHeaders
        };

        return this.sendRequest('POST', url, options, retryRequest).pipe(
            map(response => {
                // LogService.debug('POST sendRequest', response, url, options);
                return new ServiceResponse();
            }),
            catchError(err => {
                LogService.warn('invokePostWithOutResponse failed', err, url, options);
                return this.handleErrorAsResponse<ServiceResponse>(err);
            })
        );

    }

    invokePostWithResponse<TRequest extends ServiceRequest, TResponse extends ServiceResponseInterface>(
        requestUri: string,
        requestContent: TRequest,
        responseType: ClassConstructor<TResponse>,
        useFrameworkService = false,
        customHeaders: HttpHeaders = null,
        customControllerName: string = null,            // example: TipoBollaFatturaReport
        customControllerAddress: string = null,         // example: https://ntscompanydata.ntsinformatica.it/api/TipoBollaFatturaReport
        cacheOptions: CacheOptionsInterface = {
            bypass: false,
            expirationTime: undefined,
            force: false,
        },
        retryRequest: number = 0,
    ): Observable<TResponse> {

        const requestPlain = classToPlain(requestContent, { strategy: 'excludeAll' });
        const cycledRequestPlain = JsonNetDecycle.decycle(requestPlain);

        const requestOptions = {
            body: cycledRequestPlain,
            headers: customHeaders
        };

        return this.handleRequest(
            'POST',
            requestUri,
            cycledRequestPlain,
            requestOptions,
            responseType,
            useFrameworkService,
            customControllerName,
            customControllerAddress,
            cacheOptions,
            retryRequest
        );
    }

    private handleRequestWithInstanceWithCacheInfo<TResponse extends ServiceResponseInterface>(
        method: string,
        requestUri: string,
        cycledRequestPlain: Record<string, any>,
        requestOptions: any,
        responseInstance: TResponse,
        useFrameworkService: boolean = false,
        customControllerName: string = null,
        customControllerAddress: string = null,
        cacheOptions: CacheOptionsInterface = {
            bypass: false,
            expirationTime: undefined,
            force: false,
        },
        retryRequest: number = 0,
    ): Observable<ResponseWithCacheInfo<TResponse>> {
        const serviceInfo = this.getServiceInfo(useFrameworkService, customControllerName);
        const url: string = (customControllerAddress ?? serviceInfo.address) + '/' + requestUri;

        // Sono online
        if (this.onlineService.isOnline) {

            // Se non ho attivo il bypass della cache
            if (!cacheOptions.bypass) {

                // Verifico se esiste una cache salvata per questa richiesta
                return this.responseCache.check(
                    responseInstance,
                    url,
                    cycledRequestPlain ? JSON.stringify(cycledRequestPlain) : null,
                    this.onlineService.isOnline,
                    WITHOUT_EXPIRATION_TIME,
                    null,
                    true,
                    cacheOptions.tenantBarrier,
                    cacheOptions.enterpriseBarrier,
                    cacheOptions.userBarrier,
                    cacheOptions.overrideBarrierValues
                ).pipe(
                    switchMap((hasCache) => {

                        // Verifico se esiste una cache salvata e non scaduta per questa richiesta
                        return this.responseCache.check(
                            responseInstance,
                            url,
                            cycledRequestPlain ? JSON.stringify(cycledRequestPlain) : null,
                            this.onlineService.isOnline,
                            cacheOptions.expirationTime,
                            null,
                            cacheOptions.force,
                            cacheOptions.tenantBarrier,
                            cacheOptions.enterpriseBarrier,
                            cacheOptions.userBarrier,
                            cacheOptions.overrideBarrierValues
                        ).pipe(
                            switchMap((isCacheNotExpired) => {

                                // Se la cache salvata non è scaduta
                                if (isCacheNotExpired) {

                                    // Ritorno la cache non scaduta invece di effettuare la chiamata
                                    return this.responseCache.get(
                                        responseInstance,
                                        url,
                                        cycledRequestPlain ? JSON.stringify(cycledRequestPlain) : null,
                                        this.onlineService.isOnline,
                                        cacheOptions.expirationTime,
                                        null,
                                        cacheOptions.force,
                                        cacheOptions.tenantBarrier,
                                        cacheOptions.enterpriseBarrier,
                                        cacheOptions.userBarrier,
                                        cacheOptions.overrideBarrierValues
                                    ).pipe(
                                        switchMap((cachedData) => {

                                            // Recupero la response
                                            const resFromCache = this.handleResponse<TResponse>(
                                                responseInstance, cachedData, true
                                            );

                                            // Se la chiamata non è andata a buon fine
                                            if (resFromCache?.errors?.length > 0 || resFromCache?.operationSuccedeed == false) {

                                                const timeoutCacheMs = 0;

                                                // Effettuo la chiamata, passando anche i parametri per l'eventuale risposta per il timeout
                                                return this.sendRequest(
                                                    method,
                                                    url,
                                                    requestOptions,
                                                    retryRequest,
                                                    timeoutCacheMs,
                                                ).pipe(
                                                    switchMap((rawResponse: object | HttpResponse<any>) => {
                                                        LogService.debug(`${method} ${url}`, rawResponse, url, requestOptions, cycledRequestPlain);

                                                        const res = this.handleResponse<TResponse>(
                                                            responseInstance, rawResponse, true
                                                        );

                                                        // Se la chiamata è andata a buon fine
                                                        if (res?.errors?.length == 0 && res?.operationSuccedeed == true) {
                                                            // Aggiorno la cache per l'offline/timeout
                                                            this.responseCache.set(
                                                                responseInstance,
                                                                url,
                                                                rawResponse instanceof HttpResponse ? rawResponse.body : rawResponse,
                                                                cycledRequestPlain ? JSON.stringify(cycledRequestPlain) : null,
                                                                cacheOptions.force,
                                                                cacheOptions.tenantBarrier,
                                                                cacheOptions.enterpriseBarrier,
                                                                cacheOptions.userBarrier,
                                                                cacheOptions.overrideBarrierValues
                                                            ).subscribe((res) => {
                                                                // if (this.responseCacheWebworkerSettings?.showLogs) {
                                                                //     LogService.debug('Risultato salvataggio cache: ' + res)
                                                                // }
                                                            });
                                                        }

                                                        return of(res).pipe(
                                                            switchMap(async (responseData) => {
                                                                const res = new ResponseWithCacheInfo<TResponse>();
                                                                res.response = responseData;

                                                                const currentDate = new Date();
                                                                const timestamp = Math.round(currentDate.getTime() / 1000);

                                                                res.cache = { timestamp };

                                                                return res;
                                                            })
                                                        );

                                                    }),
                                                    catchError((err) => {
                                                        LogService.warn(
                                                            `${method} ${url} failed`,
                                                            err,
                                                            url,
                                                            requestOptions
                                                        );
                                                        return this.handleErrorAsResponse<TResponse>(err).pipe(map((r) => {
                                                            const res = new ResponseWithCacheInfo<TResponse>();
                                                            const currentDate = new Date();
                                                            const timestamp = Math.round(currentDate.getTime() / 1000);
                                                            res.response = r;
                                                            res.cache = {
                                                                timestamp
                                                            };
                                                            return res;
                                                        }));
                                                    })
                                                );

                                            } else {

                                                LogService.debug(
                                                    `Utilizzo la cache non scaduta per la chiamata ${method} ${url}`, cycledRequestPlain
                                                );
                                                return of(this.handleResponse<TResponse>(
                                                    responseInstance, cachedData, true
                                                )).pipe(
                                                    switchMap(async (responseData) => {
                                                        const res = new ResponseWithCacheInfo<TResponse>();
                                                        res.response = responseData;

                                                        const timestamp = await this.responseCache.getTimestampFromOffLineCacheForSpecificRequest(
                                                            url,
                                                            cycledRequestPlain ? JSON.stringify(cycledRequestPlain) : null,
                                                            cacheOptions.tenantBarrier,
                                                            cacheOptions.enterpriseBarrier,
                                                            cacheOptions.userBarrier,
                                                            cacheOptions.overrideBarrierValues
                                                        );

                                                        res.cache = { timestamp };

                                                        return res;
                                                    })
                                                );

                                            }

                                        })
                                    );

                                } else {

                                    // Se la cache salvata è scaduta
                                    const timeoutCacheMs = hasCache && cacheOptions.enableTimeout ?
                                        (cacheOptions.timeoutMillisecond ?? TIMEOUT_CACHE) :
                                        0;

                                    // Effettuo la chimata, passando anche i parametri per l'eventuale risposta per il timeout
                                    return this.sendRequest(
                                        method,
                                        url,
                                        requestOptions,
                                        retryRequest,
                                        timeoutCacheMs,
                                        this.responseCache.get(
                                            responseInstance,
                                            url,
                                            cycledRequestPlain ? JSON.stringify(cycledRequestPlain) : null,
                                            this.onlineService.isOnline,
                                            WITHOUT_EXPIRATION_TIME,
                                            null,
                                            true,
                                            cacheOptions.tenantBarrier,
                                            cacheOptions.enterpriseBarrier,
                                            cacheOptions.userBarrier,
                                            cacheOptions.overrideBarrierValues
                                        ).pipe(tap((res) => {
                                            LogService.debug(`Uso la cache a causa del timeout di ${timeoutCacheMs} per la chiamata ${method} ${url}`, cycledRequestPlain);
                                        }))
                                    ).pipe(
                                        switchMap((rawResponse: object | HttpResponse<any>) => {
                                            LogService.debug(`${method} ${url}`, rawResponse, url, requestOptions, cycledRequestPlain);

                                            const res = this.handleResponse<TResponse>(
                                                responseInstance, rawResponse, true
                                            );

                                            // Se la chiamata è andata a buon fine
                                            if (res?.errors?.length == 0 && res?.operationSuccedeed == true) {
                                                // Aggiorno la cache per l'offline/timeout
                                                this.responseCache.set(
                                                    responseInstance,
                                                    url,
                                                    rawResponse instanceof HttpResponse ? rawResponse.body : rawResponse,
                                                    cycledRequestPlain ? JSON.stringify(cycledRequestPlain) : null,
                                                    cacheOptions.force,
                                                    cacheOptions.tenantBarrier,
                                                    cacheOptions.enterpriseBarrier,
                                                    cacheOptions.userBarrier,
                                                    cacheOptions.overrideBarrierValues
                                                ).subscribe((res) => {
                                                    // if (this.responseCacheWebworkerSettings?.showLogs) {
                                                    //     LogService.debug('Risultato salvataggio cache: ' + res)
                                                    // }
                                                });
                                            }

                                            return of(res).pipe(
                                                switchMap(async (responseData) => {
                                                    const res = new ResponseWithCacheInfo<TResponse>();
                                                    res.response = responseData;

                                                    const currentDate = new Date();
                                                    const timestamp = Math.round(currentDate.getTime() / 1000);

                                                    res.cache = { timestamp };

                                                    return res;
                                                })
                                            );
                                        }),
                                        catchError((err) => {
                                            LogService.warn(
                                                `${method} ${url} failed`,
                                                err,
                                                url,
                                                requestOptions
                                            );
                                            return this.handleErrorAsResponse<TResponse>(err).pipe(map((r) => {
                                                const res = new ResponseWithCacheInfo<TResponse>();
                                                const currentDate = new Date();
                                                const timestamp = Math.round(currentDate.getTime() / 1000);
                                                res.response = r;
                                                res.cache = {
                                                    timestamp
                                                };
                                                return res;
                                            }));
                                        })
                                    );
                                }
                            })
                        );
                    })
                );
            } else {

                // Se ho attivo il bypass della cache
                return this.sendRequest(method, url, requestOptions, retryRequest).pipe(
                    switchMap((rawResponse: object | HttpResponse<any>) => {
                        LogService.debug(`${method} ${url}`, rawResponse, url, requestOptions);

                        const res = this.handleResponse<TResponse>(
                            responseInstance, rawResponse, true
                        );

                        // Se la chiamata è andata a buon fine
                        if (res?.errors?.length == 0 && res?.operationSuccedeed == true) {
                            // Aggiorno la cache per l'offline/timeout
                            this.responseCache.set(
                                responseInstance,
                                url,
                                rawResponse instanceof HttpResponse ? rawResponse.body : rawResponse,
                                cycledRequestPlain ? JSON.stringify(cycledRequestPlain) : null,
                                cacheOptions.force,
                                cacheOptions.tenantBarrier,
                                cacheOptions.enterpriseBarrier,
                                cacheOptions.userBarrier,
                                cacheOptions.overrideBarrierValues
                            ).subscribe((res) => {
                                // if (this.responseCacheWebworkerSettings?.showLogs) {
                                //     LogService.debug('Risultato salvataggio cache: ' + res)
                                // }
                            });
                        }

                        return of(res).pipe(
                            switchMap(async (responseData) => {
                                const res = new ResponseWithCacheInfo<TResponse>();
                                res.response = responseData;

                                const currentDate = new Date();
                                const timestamp = Math.round(currentDate.getTime() / 1000);

                                res.cache = { timestamp };

                                return res;
                            })
                        );
                    }),
                    catchError((err) => {
                        LogService.warn(
                            `${method} ${url} failed`,
                            err,
                            url,
                            requestOptions
                        );
                        return this.handleErrorAsResponse<TResponse>(err).pipe(map((r) => {
                            const res = new ResponseWithCacheInfo<TResponse>();
                            const currentDate = new Date();
                            const timestamp = Math.round(currentDate.getTime() / 1000);
                            res.response = r;
                            res.cache = {
                                timestamp
                            };
                            return res;
                        }));
                    })
                );
            }
        } else {
            // Sono offline
            return this.responseCache.check(
                responseInstance,
                url,
                cycledRequestPlain ? JSON.stringify(cycledRequestPlain) : null,
                this.onlineService.isOnline,
                cacheOptions.expirationTime,
                null,
                cacheOptions.force,
                cacheOptions.tenantBarrier,
                cacheOptions.enterpriseBarrier,
                cacheOptions.userBarrier,
                cacheOptions.overrideBarrierValues
            ).pipe(
                switchMap((isInCache) => {
                    // Ho la cache per l'offline
                    if (isInCache) {
                        return this.responseCache.get(
                            responseInstance,
                            url,
                            cycledRequestPlain ? JSON.stringify(cycledRequestPlain) : null,
                            this.onlineService.isOnline,
                            cacheOptions.expirationTime,
                            null,
                            cacheOptions.force,
                            cacheOptions.tenantBarrier,
                            cacheOptions.enterpriseBarrier,
                            cacheOptions.userBarrier,
                            cacheOptions.overrideBarrierValues
                        ).pipe(switchMap((cachedData) => {
                            LogService.debug(`Used cache for ${url}`);

                            return of(this.handleResponse<TResponse>(
                                responseInstance, cachedData, true
                            )).pipe(
                                switchMap(async (responseData) => {
                                    const res = new ResponseWithCacheInfo<TResponse>();
                                    res.response = responseData;

                                    const timestamp = await this.responseCache.getTimestampFromOffLineCacheForSpecificRequest(
                                        url,
                                        cycledRequestPlain ? JSON.stringify(cycledRequestPlain) : null,
                                        cacheOptions.tenantBarrier,
                                        cacheOptions.enterpriseBarrier,
                                        cacheOptions.userBarrier,
                                        cacheOptions.overrideBarrierValues
                                    );

                                    res.cache = { timestamp };

                                    return res;
                                })
                            );
                        }));

                    } else {

                        // Non ho la cache per l'offline
                        const err: any = new Error(
                            `No cache found for ${method} ${url} with sended data.`
                        );
                        LogService.warn(
                            `${method} ${url} failed in offline mode`,
                            err,
                            url,
                            cacheOptions
                        );
                        return this.handleErrorAsResponse<TResponse>(err).pipe(map((r) => {
                            const res = new ResponseWithCacheInfo<TResponse>();
                            const currentDate = new Date();
                            const timestamp = Math.round(currentDate.getTime() / 1000);
                            res.response = r;
                            res.cache = {
                                timestamp
                            };
                            return res;
                        }));
                    }
                })
            );
        }
    }

    private handleRequestWithInstance<TResponse extends ServiceResponseInterface>(
        method: string,
        requestUri: string,
        cycledRequestPlain: Record<string, any>,
        requestOptions: any,
        responseInstance: TResponse,
        useFrameworkService: boolean = false,
        customControllerName: string = null,
        customControllerAddress: string = null,
        cacheOptions: CacheOptionsInterface = {
            bypass: false,
            expirationTime: undefined,
            force: false,
        },
        retryRequest: number = 0,
    ): Observable<TResponse> {
        const serviceInfo = this.getServiceInfo(useFrameworkService, customControllerName);
        const url: string = (customControllerAddress ?? serviceInfo.address) + '/' + requestUri;

        // Sono online
        if (this.onlineService.isOnline) {

            // Se non ho attivo il bypass della cache
            if (!cacheOptions.bypass) {

                // Verifico se esiste una cache salvata per questa richiesta
                return this.responseCache.check(
                    responseInstance,
                    url,
                    cycledRequestPlain ? JSON.stringify(cycledRequestPlain) : null,
                    this.onlineService.isOnline,
                    WITHOUT_EXPIRATION_TIME,
                    null,
                    true,
                    cacheOptions.tenantBarrier,
                    cacheOptions.enterpriseBarrier,
                    cacheOptions.userBarrier,
                    cacheOptions.overrideBarrierValues
                ).pipe(
                    switchMap((hasCache) => {

                        // Verifico se esiste una cache salvata e non scaduta per questa richiesta
                        return this.responseCache.check(
                            responseInstance,
                            url,
                            cycledRequestPlain ? JSON.stringify(cycledRequestPlain) : null,
                            this.onlineService.isOnline,
                            cacheOptions.expirationTime,
                            null,
                            cacheOptions.force,
                            cacheOptions.tenantBarrier,
                            cacheOptions.enterpriseBarrier,
                            cacheOptions.userBarrier,
                            cacheOptions.overrideBarrierValues
                        ).pipe(
                            switchMap((isCacheNotExpired) => {

                                // Se la cache salvata non è scaduta
                                if (isCacheNotExpired) {

                                    // Ritorno la cache non scaduta invece di effettuare la chiamata
                                    return this.responseCache.get(
                                        responseInstance,
                                        url,
                                        cycledRequestPlain ? JSON.stringify(cycledRequestPlain) : null,
                                        this.onlineService.isOnline,
                                        cacheOptions.expirationTime,
                                        null,
                                        cacheOptions.force,
                                        cacheOptions.tenantBarrier,
                                        cacheOptions.enterpriseBarrier,
                                        cacheOptions.userBarrier,
                                        cacheOptions.overrideBarrierValues
                                    ).pipe(
                                        switchMap((cachedData) => {

                                            // Recupero la response
                                            const resFromCache = this.handleResponse<TResponse>(
                                                responseInstance, cachedData, true
                                            );

                                            // Se la chiamata non è andata a buon fine
                                            if (resFromCache?.errors?.length > 0 || resFromCache?.operationSuccedeed == false) {

                                                const timeoutCacheMs = 0;

                                                // Effettuo la chiamata
                                                return this.sendRequest(
                                                    method,
                                                    url,
                                                    requestOptions,
                                                    retryRequest,
                                                    timeoutCacheMs,
                                                ).pipe(
                                                    switchMap((rawResponse: object | HttpResponse<any>) => {
                                                        LogService.debug(`${method} ${url}`, rawResponse, url, requestOptions, cycledRequestPlain);

                                                        // Recupero la response
                                                        const res = this.handleResponse<TResponse>(
                                                            responseInstance, rawResponse, true
                                                        );

                                                        // Se la chiamata è andata a buon fine
                                                        if (res?.errors?.length == 0 && res?.operationSuccedeed == true) {
                                                            // Aggiorno la cache per l'offline/timeout
                                                            this.responseCache.set(
                                                                responseInstance,
                                                                url,
                                                                rawResponse instanceof HttpResponse ? rawResponse.body : rawResponse,
                                                                cycledRequestPlain ? JSON.stringify(cycledRequestPlain) : null,
                                                                cacheOptions.force,
                                                                cacheOptions.tenantBarrier,
                                                                cacheOptions.enterpriseBarrier,
                                                                cacheOptions.userBarrier,
                                                                cacheOptions.overrideBarrierValues
                                                            ).subscribe((res) => {
                                                                // if (this.responseCacheWebworkerSettings?.showLogs) {
                                                                //     LogService.debug('Risultato salvataggio cache: ' + res)
                                                                // }
                                                            });
                                                        }

                                                        return of(res);
                                                    }),
                                                    catchError((err) => {
                                                        LogService.warn(
                                                            `${method} ${url} failed`,
                                                            err,
                                                            url,
                                                            requestOptions
                                                        );
                                                        return this.handleErrorAsResponse<TResponse>(err);
                                                    })
                                                );

                                            } else {
                                                LogService.debug(
                                                    `Utilizzo la cache non scaduta per la chiamata ${method} ${url}`, cycledRequestPlain
                                                );
                                                return of(resFromCache);
                                            }
                                        })
                                    );

                                } else {

                                    // Se la cache salvata è scaduta
                                    const timeoutCacheMs = hasCache && cacheOptions.enableTimeout ?
                                        (cacheOptions.timeoutMillisecond ?? TIMEOUT_CACHE) :
                                        0;

                                    // Effettuo la chimata, passando anche i parametri per l'eventuale risposta per il timeout
                                    return this.sendRequest(
                                        method,
                                        url,
                                        requestOptions,
                                        retryRequest,
                                        timeoutCacheMs,
                                        this.responseCache.get(
                                            responseInstance,
                                            url,
                                            cycledRequestPlain ? JSON.stringify(cycledRequestPlain) : null,
                                            this.onlineService.isOnline,
                                            WITHOUT_EXPIRATION_TIME,
                                            null,
                                            true,
                                            cacheOptions.tenantBarrier,
                                            cacheOptions.enterpriseBarrier,
                                            cacheOptions.userBarrier,
                                            cacheOptions.overrideBarrierValues
                                        ).pipe(tap((res) => {
                                            LogService.debug(`Uso la cache a causa del timeout di ${timeoutCacheMs} per la chiamata ${method} ${url}`, cycledRequestPlain);
                                        }))
                                    ).pipe(
                                        switchMap((rawResponse: object | HttpResponse<any>) => {
                                            LogService.debug(`${method} ${url}`, rawResponse, url, requestOptions, cycledRequestPlain);

                                            const res = this.handleResponse<TResponse>(
                                                responseInstance, rawResponse, true
                                            );

                                            // Se non devo bypassare la cache
                                            if (!cacheOptions.bypass) {

                                                // Se la chiamata è andata a buon fine
                                                if (res?.errors?.length == 0 && res?.operationSuccedeed == true) {

                                                    // Aggiorno la cache per l'offline/timeout
                                                    this.responseCache.set(
                                                        responseInstance,
                                                        url,
                                                        rawResponse instanceof HttpResponse ? rawResponse.body : rawResponse,
                                                        cycledRequestPlain ? JSON.stringify(cycledRequestPlain) : null,
                                                        cacheOptions.force,
                                                        cacheOptions.tenantBarrier,
                                                        cacheOptions.enterpriseBarrier,
                                                        cacheOptions.userBarrier,
                                                        cacheOptions.overrideBarrierValues
                                                    ).subscribe((res) => {
                                                        // if (this.responseCacheWebworkerSettings?.showLogs) {
                                                        //     LogService.debug('Risultato salvataggio cache: ' + res)
                                                        // }
                                                    });

                                                }
                                            }
                                            return of(res);
                                        }),
                                        catchError((err) => {
                                            LogService.warn(
                                                `${method} ${url} failed`,
                                                err,
                                                url,
                                                requestOptions
                                            );
                                            return this.handleErrorAsResponse<TResponse>(err);
                                        })
                                    );
                                }
                            })
                        );
                    })
                );
            } else {

                // Se ho attivo il bypass della cache
                return this.sendRequest(method, url, requestOptions, retryRequest).pipe(
                    switchMap((response) => {
                        LogService.debug(`${method} ${url}`, response, url, requestOptions);
                        return of(this.handleResponse<TResponse>(
                            responseInstance, response, true
                        ));
                    }),
                    catchError((err) => {
                        LogService.warn(
                            `${method} ${url} failed`,
                            err,
                            url,
                            requestOptions
                        );
                        return this.handleErrorAsResponse<TResponse>(err);
                    })
                );
            }
        } else {
            // Sono offline
            return this.responseCache.check(
                responseInstance,
                url,
                cycledRequestPlain ? JSON.stringify(cycledRequestPlain) : null,
                this.onlineService.isOnline,
                cacheOptions.expirationTime,
                null,
                cacheOptions.force,
                cacheOptions.tenantBarrier,
                cacheOptions.enterpriseBarrier,
                cacheOptions.userBarrier,
                cacheOptions.overrideBarrierValues
            ).pipe(
                switchMap((isInCache) => {
                    // Ho la cache per l'offline
                    if (isInCache) {
                        return this.responseCache.get(
                            responseInstance,
                            url,
                            cycledRequestPlain ? JSON.stringify(cycledRequestPlain) : null,
                            this.onlineService.isOnline,
                            cacheOptions.expirationTime,
                            null,
                            cacheOptions.force,
                            cacheOptions.tenantBarrier,
                            cacheOptions.enterpriseBarrier,
                            cacheOptions.userBarrier,
                            cacheOptions.overrideBarrierValues
                        ).pipe(switchMap((cachedData) => {
                            LogService.debug(`Used cache for ${url}`);
                            return of(this.handleResponse<TResponse>(
                                responseInstance, cachedData, true
                            ));
                        }));

                    } else {

                        // Non ho la cache per l'offline
                        const err: any = new Error(
                            `No cache found for ${method} ${url} with sended data.`
                        );
                        LogService.warn(
                            `${method} ${url} failed in offline mode`,
                            err,
                            url,
                            cacheOptions
                        );
                        return this.handleErrorAsResponse<TResponse>(err);
                    }
                })
            );
        }
    }

    private handleRequest<TResponse extends ServiceResponseInterface>(
        method: string,
        requestUri: string,
        cycledRequestPlain: Record<string, any>,
        requestOptions: any,
        responseType: ClassConstructor<TResponse>,
        useFrameworkService: boolean = false,
        customControllerName: string = null,
        customControllerAddress: string = null,
        cacheOptions: CacheOptionsInterface = {
            bypass: false,
            expirationTime: undefined,
            force: false,
        },
        retryRequest: number = 0,
    ) {
        const serviceInfo = this.getServiceInfo(useFrameworkService, customControllerName);
        const url: string = (customControllerAddress ?? serviceInfo.address) + '/' + requestUri;

        // Sono online
        if (this.onlineService.isOnline) {

            // Se non ho attivo il bypass della cache
            if (!cacheOptions.bypass) {

                // Verifico se esiste una cache salvata per questa richiesta
                return this.responseCache.check(
                    responseType,
                    url,
                    cycledRequestPlain ? JSON.stringify(cycledRequestPlain) : null,
                    this.onlineService.isOnline,
                    WITHOUT_EXPIRATION_TIME,
                    null,
                    true,
                    cacheOptions.tenantBarrier,
                    cacheOptions.enterpriseBarrier,
                    cacheOptions.userBarrier,
                    cacheOptions.overrideBarrierValues
                ).pipe(
                    switchMap((hasCache) => {

                        // Verifico se esiste una cache salvata e non scaduta per questa richiesta
                        return this.responseCache.check(
                            responseType,
                            url,
                            cycledRequestPlain ? JSON.stringify(cycledRequestPlain) : null,
                            this.onlineService.isOnline,
                            cacheOptions.expirationTime,
                            null,
                            cacheOptions.force,
                            cacheOptions.tenantBarrier,
                            cacheOptions.enterpriseBarrier,
                            cacheOptions.userBarrier,
                            cacheOptions.overrideBarrierValues
                        ).pipe(
                            switchMap((isCacheNotExpired) => {

                                // Se la cache salvata non è scaduta
                                if (isCacheNotExpired) {

                                    // Ritorno la cache non scaduta invece di effettuare la chiamata
                                    return this.responseCache.get(
                                        responseType,
                                        url,
                                        cycledRequestPlain ? JSON.stringify(cycledRequestPlain) : null,
                                        this.onlineService.isOnline,
                                        cacheOptions.expirationTime,
                                        null,
                                        cacheOptions.force,
                                        cacheOptions.tenantBarrier,
                                        cacheOptions.enterpriseBarrier,
                                        cacheOptions.userBarrier,
                                        cacheOptions.overrideBarrierValues
                                    ).pipe(
                                        switchMap((cachedData) => {

                                            // Recupero la response
                                            const resFromCache = this.handleResponse<TResponse>(
                                                responseType, cachedData, false
                                            );

                                            // Se la chiamata non è andata a buon fine
                                            if (resFromCache?.errors?.length > 0 || resFromCache?.operationSuccedeed == false) {

                                                const timeoutCacheMs = 0;

                                                // Effettuo la chimata
                                                return this.sendRequest(
                                                    method,
                                                    url,
                                                    requestOptions,
                                                    retryRequest,
                                                    timeoutCacheMs,

                                                ).pipe(
                                                    switchMap((rawResponse: object | HttpResponse<any>) => {
                                                        LogService.debug(`${method} ${url}`, rawResponse, url, requestOptions);

                                                        const res = this.handleResponse<TResponse>(
                                                            responseType, rawResponse, false
                                                        );

                                                        // Se la chiamata è andata a buon fine
                                                        if (res?.errors?.length == 0 && res?.operationSuccedeed == true) {
                                                            // Aggiorno la cache per l'offline/timeout
                                                            this.responseCache.set(
                                                                responseType,
                                                                url,
                                                                rawResponse instanceof HttpResponse ? rawResponse.body : rawResponse,
                                                                cycledRequestPlain ? JSON.stringify(cycledRequestPlain) : null,
                                                                cacheOptions.force,
                                                                cacheOptions.tenantBarrier,
                                                                cacheOptions.enterpriseBarrier,
                                                                cacheOptions.userBarrier,
                                                                cacheOptions.overrideBarrierValues
                                                            ).subscribe((res) => {
                                                                // if (this.responseCacheWebworkerSettings?.showLogs) {
                                                                //     LogService.debug('Risultato salvataggio cache: ' + res, cycledRequestPlain)
                                                                // }
                                                            });
                                                        }

                                                        return of(res);
                                                    }),
                                                    catchError((err) => {
                                                        LogService.warn(
                                                            `${method} ${url} failed`,
                                                            err,
                                                            url,
                                                            requestOptions
                                                        );
                                                        return this.handleErrorAsResponse<TResponse>(err);
                                                    })
                                                );

                                            } else {
                                                LogService.debug(
                                                    `Utilizzo la cache non scaduta per la chiamata ${method} ${url}`, cycledRequestPlain
                                                );
                                                return of(resFromCache);
                                            }
                                        })
                                    );

                                } else {

                                    // Se la cache salvata è scaduta
                                    const timeoutCacheMs = hasCache && cacheOptions.enableTimeout ?
                                        (cacheOptions.timeoutMillisecond ?? TIMEOUT_CACHE) :
                                        0;

                                    // Effettuo la chiamata, passando anche i parametri per l'eventuale risposta per il timeout
                                    return this.sendRequest(
                                        method,
                                        url,
                                        requestOptions,
                                        retryRequest,
                                        timeoutCacheMs,
                                        this.responseCache.get(
                                            responseType,
                                            url,
                                            cycledRequestPlain ? JSON.stringify(cycledRequestPlain) : null,
                                            this.onlineService.isOnline,
                                            WITHOUT_EXPIRATION_TIME,
                                            null,
                                            true,
                                            cacheOptions.tenantBarrier,
                                            cacheOptions.enterpriseBarrier,
                                            cacheOptions.userBarrier,
                                            cacheOptions.overrideBarrierValues
                                        ).pipe(tap((res) => {
                                            LogService.debug(`Uso la cache a causa del timeout di ${timeoutCacheMs} per la chiamata ${method} ${url}`, cycledRequestPlain);
                                        }))
                                    ).pipe(
                                        switchMap((rawResponse: object | HttpResponse<any>) => {
                                            LogService.debug(`${method} ${url}`, rawResponse, url, requestOptions);

                                            const res = this.handleResponse<TResponse>(
                                                responseType, rawResponse, false
                                            );

                                            // Se non devo bypassare la cache
                                            if (!cacheOptions.bypass) {

                                                // Se la chiamata è andata a buon fine
                                                if (res?.errors?.length == 0 && res?.operationSuccedeed == true) {
                                                    // Aggiorno la cache per l'offline/timeout
                                                    this.responseCache.set(
                                                        responseType,
                                                        url,
                                                        rawResponse instanceof HttpResponse ? rawResponse.body : rawResponse,
                                                        cycledRequestPlain ? JSON.stringify(cycledRequestPlain) : null,
                                                        cacheOptions.force,
                                                        cacheOptions.tenantBarrier,
                                                        cacheOptions.enterpriseBarrier,
                                                        cacheOptions.userBarrier,
                                                        cacheOptions.overrideBarrierValues
                                                    ).subscribe((res) => {
                                                        // if (this.responseCacheWebworkerSettings?.showLogs) {
                                                        //     LogService.debug('Risultato salvataggio cache: ' + res, cycledRequestPlain)
                                                        // }
                                                    });
                                                }
                                            }
                                            return of(res);
                                        }),
                                        catchError((err) => {
                                            LogService.warn(
                                                `${method} ${url} failed`,
                                                err,
                                                url,
                                                requestOptions
                                            );
                                            return this.handleErrorAsResponse<TResponse>(err);
                                        })
                                    );
                                }
                            })
                        );
                    })
                );
            } else {

                // Se ho attivo il bypass della cache
                return this.sendRequest(method, url, requestOptions, retryRequest).pipe(
                    switchMap((response) => {
                        LogService.debug(`${method} ${url}`, response, url, requestOptions, cycledRequestPlain);
                        return of(this.handleResponse<TResponse>(
                            responseType, response, false
                        ));
                    }),
                    catchError((err) => {
                        LogService.warn(
                            `${method} ${url} failed`,
                            err,
                            url,
                            requestOptions,
                            cycledRequestPlain
                        );
                        return this.handleErrorAsResponse<TResponse>(err);
                    })
                );
            }
        } else {
            // Sono offline
            return this.responseCache.check(
                responseType,
                url,
                cycledRequestPlain ? JSON.stringify(cycledRequestPlain) : null,
                this.onlineService.isOnline,
                cacheOptions.expirationTime,
                null,
                cacheOptions.force,
                cacheOptions.tenantBarrier,
                cacheOptions.enterpriseBarrier,
                cacheOptions.userBarrier,
                cacheOptions.overrideBarrierValues
            ).pipe(
                switchMap((isInCache) => {
                    // Ho la cache per l'offline
                    if (isInCache) {
                        return this.responseCache.get(
                            responseType,
                            url,
                            cycledRequestPlain ? JSON.stringify(cycledRequestPlain) : null,
                            this.onlineService.isOnline,
                            cacheOptions.expirationTime,
                            null,
                            cacheOptions.force,
                            cacheOptions.tenantBarrier,
                            cacheOptions.enterpriseBarrier,
                            cacheOptions.userBarrier,
                            cacheOptions.overrideBarrierValues
                        ).pipe(switchMap((cachedData) => {
                            LogService.debug(`Used cache for ${url}`);
                            return of(this.handleResponse<TResponse>(
                                responseType, cachedData, false
                            ));
                        }));

                    } else {

                        // Non ho la cache per l'offline
                        const err: any = new Error(
                            `No cache found for ${method} ${url} with sended data.`
                        );
                        LogService.warn(
                            `${method} ${url} failed in offline mode`,
                            err,
                            url,
                            cycledRequestPlain,
                            cacheOptions
                        );
                        return this.handleErrorAsResponse<TResponse>(err);
                    }
                })
            );
        }

    }

    private handleResponse<TResponse>(
        responseTypeOrInstance: TResponse | ClassConstructor<TResponse>,
        rawResponse: object | HttpResponse<any>,
        fromInstance = false,
        useWorker = false
    ): TResponse {
        let res: TResponse = null;

        if (rawResponse instanceof HttpResponse) {
            res = (fromInstance ?
                plainToClassFromExist<TResponse, object>(responseTypeOrInstance as TResponse, rawResponse.body, { strategy: 'excludeAll' }) :
                plainToClass<TResponse, object>(responseTypeOrInstance as ClassConstructor<TResponse>, rawResponse.body, { strategy: 'excludeAll' })) as TResponse;

            if (res['errors'] && res['errors']?.length > 0 && res instanceof ServiceResponse) {
                for (const err of res.errors) {
                    if (err instanceof BaseError) {
                        err.requestInfo = rawResponse['requestInfo'];
                        err.responseInfo = {
                            headers: [],
                            status: rawResponse.status,
                            response: rawResponse.body
                        };

                        if (rawResponse.headers != null) {
                            err.responseInfo.headers = rawResponse.headers.keys().map(
                                (key: string) => {
                                    let headerValue = rawResponse.headers.get(key);
                                    try {
                                        headerValue = JSON.parse(headerValue);
                                        return { [key]: headerValue };
                                    } catch (err) {
                                        return { [key]: rawResponse.headers.get(key) };
                                    }
                                }
                            ).filter((v) => v);
                        }

                    }

                }
            }

        } else {
            res = (fromInstance ?
                plainToClassFromExist<TResponse, object>(responseTypeOrInstance as TResponse, rawResponse, { strategy: 'excludeAll' }) :
                plainToClass<TResponse, object>(responseTypeOrInstance as ClassConstructor<TResponse>, rawResponse, { strategy: 'excludeAll' })) as TResponse;
        }

        return res;
    }

    private shouldRetry(error: HttpErrorResponse) {
        // Se viene ritornato un 50X aumento il tempo di retry a 15 secondi
        if (error.status >= 500) {
            return timer(15000); // Adding a timer from RxJS to return observable to delay param.
        } else { // Di norma il tempo di retry è 3 secondi
            return timer(3000);
        }
        // throw error;
    }

    private sendRequest(
        method: string,
        url: string,
        options: any = {},
        retryRequest = 0,
        timeoutCache = 0,
        timeoutCacheFallbackObservable: Observable<any> = null
    ): Observable<any> {
        options.observe = options.observe || 'response';
        return this.httpService.request(method, url, options).pipe(
            timeoutCache > 0 ?
                timeout({
                    first: timeoutCache,
                    with: () => timeoutCacheFallbackObservable.pipe(map((d) => {
                        if (d) {
                            d['fromTimeoutCache'] = 'true';
                        }
                        return d;
                    }))
                }) :
                identity,



            // identity vuol dire che x => x, ripassa l'obs esattamente senza modifiche
            retryRequest > 0 ? retry({
                count: retryRequest,
                delay: this.shouldRetry
            }) : identity,
            map<any, any>((response: HttpResponse<any>) => {
                if (response && response['fromTimeoutCache'] === 'true') {
                    delete response['fromTimeoutCache'];
                    return response;
                }

                if (options.observe === 'events') {
                    return this.extractJson(response, true);
                } else if (options.responseType === 'text') {
                    return this.extractString(response);
                } else {
                    return this.extractJson(response, true);
                }
            }),
            shareReplay()
        );
    }

    private extractString(res: HttpResponse<string>) {

        let bodyText = '';

        if (res.status < 200 || res.status >= 300) {
            bodyText = 'Bad response status: ' + res.status;
        } else {
            // if there is some content, parse it
            if (res.status !== 204) {
                bodyText = res.body;
            }
        }

        return bodyText;
    }

    private extractJson(res: HttpResponse<any>, returnAll = false) {

        let body = {};

        if (res.status < 200 || res.status >= 300) {
            // bodyText = 'Bad response status: ' + res.status;
            body = {
                OperationSuccedeed: false,
                Errors: [{ Message: 'Bad response status: ' + res.status }],
                Informations: []
            };

        } else {
            // if there is some content, parse it
            if (res.status !== 204) {

                // devo fare il retrocycle solo del body altrimenti gli id delle altre properties lo fanno fallire (headesr request info ecc)
                if (res?.body) {
                    const retrocycledBody = JsonNetDecycle.retrocycle(res.body);
                    res = res.clone({ body: retrocycledBody });
                }
                body = returnAll ? res : res.body;
            }
        }

        return body;
    }

    private handleErrorAsResponse<TResponse extends ServiceResponseInterface>(error: any): Observable<TResponse> {

        const serviceResponse: ServiceResponseInterface = new ServiceResponse();

        return this.handleErrorAsResponseWithInstance(error, serviceResponse as TResponse);
    }

    private handleErrorAsString(error: HttpErrorResponse): Observable<string> {

        // TODO: Approfondire se possiamo valorizzare ulterioremente l'errore
        let errorMessage: string = error.error || 'ServiceAgent Error';

        if (error.status) {
            errorMessage += ' status: ' + error.status;
        }

        return of(errorMessage);
    }

    private blobToString(b) {
        var u, x;
        u = URL.createObjectURL(b);
        x = new XMLHttpRequest();
        x.open('GET', u, false); // although sync, you're not fetching over internet
        x.send();
        URL.revokeObjectURL(u);
        return x.responseText;
    }

    private handleErrorAsResponseWithInstance<TResponse extends ServiceResponseInterface>(
        errorResponse: HttpErrorResponse, responseInstance: TResponse): Observable<TResponse> {
        if (errorResponse.status === 403) {
            const errCode = MessageCodes.UnauthorizedHttpStatusCode;
            const errMsg = errorResponse.error || MessageResourceManager.Current.getMessage(errCode);
            const err = new BaseError();

            err.requestInfo = errorResponse['requestInfo'];
            err.responseInfo = {
                headers: errorResponse.headers.keys().map(
                    (key: string) => {
                        let headerValue = errorResponse.headers.get(key);
                        try {
                            headerValue = JSON.parse(headerValue);
                            return { [key]: headerValue };
                        } catch (err) {
                            return { [key]: errorResponse.headers.get(key) };
                        }
                    }
                ).filter((v) => v),
                status: errorResponse.status,
                response: errorResponse.error
            };

            err.code = errCode;
            err.description = errMsg;
            const responseUnauthorized: ServiceResponseInterface = new ServiceResponse();
            responseUnauthorized.errors.push(err);
            return of(responseUnauthorized as TResponse);
        }

        if (errorResponse.status === 500) {
            const errCode = MessageCodes.InternalServerErrorHttpStatusCode;
            const errMsg = errorResponse.error || MessageResourceManager.Current.getMessage(errCode);
            const err = new BaseError();

            err.requestInfo = errorResponse['requestInfo'];
            err.responseInfo = {
                headers: errorResponse.headers.keys().map(
                    (key: string) => {
                        let headerValue = errorResponse.headers.get(key);
                        try {
                            headerValue = JSON.parse(headerValue);
                            return { [key]: headerValue };
                        } catch (err) {
                            return { [key]: errorResponse.headers.get(key) };
                        }
                    }
                ).filter((v) => v),
                status: errorResponse.status,
                response: errorResponse.error
            };

            err.code = errCode;
            err.description = errMsg;
            const responseInternalServerError: ServiceResponseInterface = new ServiceResponse();
            responseInternalServerError.errors.push(err);
            return of(responseInternalServerError as TResponse);
        }

        if (errorResponse.status === 400) {
            const errCode = MessageCodes.BadRequestHttpStatusCode;
            const errMsg = errorResponse.error || MessageResourceManager.Current.getMessage(errCode);
            const err = new BaseError();

            err.requestInfo = errorResponse['requestInfo'];
            err.responseInfo = {
                headers: errorResponse.headers.keys().map(
                    (key: string) => {
                        let headerValue = errorResponse.headers.get(key);
                        try {
                            headerValue = JSON.parse(headerValue);
                            return { [key]: headerValue };
                        } catch (err) {
                            return { [key]: errorResponse.headers.get(key) };
                        }
                    }
                ).filter((v) => v),
                status: errorResponse.status,
                response: errorResponse.error
            };

            err.code = errCode;
            err.description = errMsg;
            const responseBadRequest: ServiceResponseInterface = new ServiceResponse();
            responseBadRequest.errors.push(err);
            return of(responseBadRequest as TResponse);
        }

        if (errorResponse.status === 404) {
            const errCode = MessageCodes.NotFoundHttpStatusCode;
            const errMsg = MessageResourceManager.Current.getMessage(errCode);
            const err = new BaseError();
            err.code = errCode;
            if (errorResponse?.error?.text) {
                err.description = this.blobToString(errorResponse.error);
            } else {
                err.description = errMsg;
            }

            err.requestInfo = errorResponse['requestInfo'];
            err.responseInfo = {
                headers: errorResponse.headers.keys().map(
                    (key: string) => {
                        let headerValue = errorResponse.headers.get(key);
                        try {
                            headerValue = JSON.parse(headerValue);
                            return { [key]: headerValue };
                        } catch (err) {
                            return { [key]: errorResponse.headers.get(key) };
                        }
                    }
                ).filter((v) => v),
                status: errorResponse.status,
                response: errorResponse.error
            };

            const responseNotFound: ServiceResponseInterface = new ServiceResponse();
            responseNotFound.errors.push(err);
            return of(responseNotFound as TResponse);
        }

        const messageError = new BaseError();

        let errorMessage = '';
        if (errorResponse.error && errorResponse.error.Message) {
            errorMessage += errorResponse.error.Message;
        } else if (errorResponse.error?.length > 0) {
            errorMessage += errorResponse.error;
        } else if (errorResponse.message) {
            errorMessage += errorResponse.message;
            messageError.stackTrace = (errorResponse as Error).stack;
        } else {
            errorMessage = 'ServiceAgent Error';
        }

        messageError.description = errorMessage;

        messageError.requestInfo = errorResponse['requestInfo'];
        messageError.responseInfo = {
            headers: errorResponse?.headers?.keys().map(
                (key: string) => {
                    let headerValue = errorResponse.headers.get(key);
                    try {
                        headerValue = JSON.parse(headerValue);
                        return { [key]: headerValue };
                    } catch (err) {
                        return { [key]: errorResponse.headers.get(key) };
                    }
                }
            ).filter((v) => v),
            status: errorResponse.status,
            response: errorResponse.error
        };

        responseInstance.operationSuccedeed = false;
        responseInstance.informations = [];
        responseInstance.errors = [messageError];

        return of(responseInstance);
    }
}
